import LanguageIcon from "@mui/icons-material/LanguageRounded";
import TodayIcon from "@mui/icons-material/TodayRounded";
import {
  Avatar,
  Box,
  Button,
  Checkbox,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { DateTimePicker } from "@mui/x-date-pickers-pro";
import { formatISO, isAfter, parseISO } from "date-fns";
import { useState } from "react";
import { withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import DialogButtons from "@entrilia/shared/components/DialogButtons";
import DialogCloseButton from "@entrilia/shared/components/DialogCloseButton";
import SmallDialog from "@entrilia/shared/components/SmallDialog";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext";
import { logError } from "@entrilia/shared/logging";
import adminApi from "../../../../../api/adminApi";
import type {
  DataCollectionRequestDetails,
  DataCollectionRequestStatus,
  PublishRequestRequest,
} from "../../../../../api/types/dataCollectionTypes";
import UnpublishIcon from "@entrilia/shared/icons/UnpublishIcon";
import BorderedRadioLabel from "../../../../common/BorderedRadioLabel";

const publishDataCollectionRequest = withErrorHandling(adminApi.publishDataCollectionRequest);
const cancelScheduledPublishOfDataRequest = withErrorHandling(adminApi.cancelScheduledPublishOfDataRequest);

interface Props {
  dataRequestId: string;
  dataRequestStatus: DataCollectionRequestStatus;
  dataRequestScheduledAt: string | undefined;
  onClose: () => void;
  onSaved: (dataRequest: DataCollectionRequestDetails) => void;
}

type PublishOption = "publish" | "schedule" | "unschedule";

const PublishDataRequestDialog = ({
  dataRequestId,
  dataRequestStatus,
  dataRequestScheduledAt,
  onClose,
  onSaved,
}: Props) => {
  const { sendNotification, sendNotificationError } = useNotificationContext();

  const [loading, setLoading] = useState(false);
  const [sendEmailNotifications, setSendEmailNotifications] = useState(true);
  const [userComment, setUserComment] = useState("");

  const [selectedOption, setSelectedOption] = useState<PublishOption>(
    dataRequestStatus === "Scheduled" ? "schedule" : "publish"
  );

  const [scheduleDateTime, setScheduleDateTime] = useState<Date | null>(() =>
    dataRequestScheduledAt ? parseISO(dataRequestScheduledAt) : null
  );

  const handlePublish = async () => {
    setLoading(true);

    const payload: PublishRequestRequest = {
      sendEmailNotifications,
      scheduledToBePublishedAt:
        selectedOption === "schedule" && scheduleDateTime !== null ? formatISO(scheduleDateTime) : undefined,
      commentUserInput: sendEmailNotifications && userComment.trim() ? userComment.trim() : undefined,
    };

    const [resp, error] = await publishDataCollectionRequest(dataRequestId, payload);

    setLoading(false);

    if (error) {
      logError(error, "[PublishDataRequestDialog] publishDataCollectionRequest");
      sendNotificationError(error.message);
      return;
    }

    sendNotification(
      selectedOption === "schedule" ? "Data request scheduled successfully" : "Data request published successfully"
    );
    onSaved(resp);
  };

  const handleUnschedule = async () => {
    setLoading(true);

    const [resp, error] = await cancelScheduledPublishOfDataRequest(dataRequestId);

    setLoading(false);

    if (error) {
      logError(error, "[PublishDataRequestDialog] cancelScheduledPublishOfDataRequest");
      sendNotificationError(error.message);
      return;
    }

    sendNotification("The scheduled publishing canceled successfully");
    onSaved(resp);
  };

  const handleSubmit = async () => {
    if (selectedOption === "unschedule") {
      await handleUnschedule();
    } else {
      await handlePublish();
    }
  };

  // eslint-disable-next-line @eslint-react/purity -- compare with the current time, not the time the dialog opened
  const isScheduledInFuture = scheduleDateTime !== null && isAfter(scheduleDateTime, new Date());
  const isValid = selectedOption === "schedule" ? isScheduledInFuture : true;

  return (
    <SmallDialog open onClose={onClose}>
      <DialogTitle>Publishing</DialogTitle>
      <DialogCloseButton onClick={onClose} />
      <Divider />
      <DialogContent>
        <Stack spacing={1}>
          <FormControl>
            <RadioGroup
              value={selectedOption}
              onChange={(e) => setSelectedOption(e.target.value as PublishOption)}
              sx={{ rowGap: 1 }}
            >
              <BorderedRadioLabel<PublishOption>
                value="publish"
                selectedValue={selectedOption}
                sx={{ pl: 2, py: 1 }}
                label={
                  <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
                    <Avatar variant="rounded">
                      <LanguageIcon color={selectedOption === "publish" ? "primary" : "action"} />
                    </Avatar>
                    <Box>
                      <Typography variant="subtitle2">Publish immediately</Typography>
                      <Typography color="textSecondary">
                        The request will be sent immediately to the recipients.
                      </Typography>
                    </Box>
                  </Stack>
                }
              />

              <BorderedRadioLabel<PublishOption>
                value="schedule"
                selectedValue={selectedOption}
                sx={{ pl: 2, py: 1 }}
                label={
                  <Stack spacing={2}>
                    <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
                      <Avatar variant="rounded">
                        <TodayIcon color={selectedOption === "schedule" ? "primary" : "action"} />
                      </Avatar>
                      <Box>
                        <Typography variant="subtitle2">Schedule for later</Typography>
                        <Typography color="textSecondary">
                          Set up schedule to send your request at specific time.
                        </Typography>
                      </Box>
                    </Stack>

                    {selectedOption === "schedule" && (
                      <DateTimePicker
                        label="Date and Time"
                        value={scheduleDateTime}
                        onChange={(newValue) => setScheduleDateTime(newValue)}
                        disablePast
                        timeSteps={{ hours: 1, minutes: 30 }}
                      />
                    )}
                  </Stack>
                }
              />

              {dataRequestStatus === "Scheduled" && (
                <BorderedRadioLabel<PublishOption>
                  value="unschedule"
                  selectedValue={selectedOption}
                  sx={{ pl: 2, py: 1 }}
                  label={
                    <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
                      <Avatar variant="rounded">
                        <UnpublishIcon color={selectedOption === "unschedule" ? "primary" : "action"} />
                      </Avatar>
                      <Box>
                        <Typography variant="subtitle2">Cancel schedule</Typography>
                        <Typography color="textSecondary">
                          The submissions will be saved as a draft, and can be published later.
                        </Typography>
                      </Box>
                    </Stack>
                  }
                />
              )}
            </RadioGroup>
          </FormControl>

          {selectedOption !== "unschedule" && (
            <FormControlLabel
              label={
                selectedOption === "publish"
                  ? "Send notification emails"
                  : "Send notification emails on publishing date"
              }
              control={
                <Checkbox
                  checked={sendEmailNotifications}
                  onChange={(_, checked) => setSendEmailNotifications(checked)}
                />
              }
            />
          )}

          {selectedOption !== "unschedule" && sendEmailNotifications && (
            <TextField
              multiline
              rows={3}
              label="Include message (optional)"
              value={userComment}
              onChange={(e) => setUserComment(e.target.value)}
            />
          )}
        </Stack>
      </DialogContent>
      <Divider />
      <DialogButtons onCancel={onClose}>
        <Button variant="contained" loading={loading} onClick={handleSubmit} disabled={!isValid}>
          {selectedOption === "publish" ? "Publish" : "Save"}
        </Button>
      </DialogButtons>
    </SmallDialog>
  );
};

export default PublishDataRequestDialog;
